/**
 * Pretium+ Offline IndexedDB Layer
 * Stores pending mutations for FNOL, Motor Claims, Breakdown, and CS Calls
 * when the app is offline, then replays them when connectivity restores.
 */

import { openDB } from 'idb';
import type { IDBPDatabase } from 'idb';

export type SyncStatus = 'pending' | 'syncing' | 'synced' | 'failed';
export type OfflineRecordType = 'fnol' | 'motor_claim' | 'breakdown_job' | 'cs_call';

export interface OfflineRecord {
  id: string;           // local UUID
  type: OfflineRecordType;
  payload: Record<string, unknown>;
  createdAt: string;
  syncStatus: SyncStatus;
  syncAttempts: number;
  syncError?: string;
  syncedAt?: string;
}

interface PretiumOfflineDB {
  sync_queue: {
    key: string;
    value: OfflineRecord;
    indexes: {
      by_status: SyncStatus;
      by_type: OfflineRecordType;
    };
  };
  cached_data: {
    key: string;
    value: {
      key: string;
      data: unknown;
      cachedAt: string;
      ttlMs: number;
    };
  };
}

let dbInstance: IDBPDatabase<PretiumOfflineDB> | null = null;

export async function getOfflineDB(): Promise<IDBPDatabase<PretiumOfflineDB>> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<PretiumOfflineDB>('pretium-offline', 1, {
    upgrade(db) {
      // Sync queue store
      const syncStore = db.createObjectStore('sync_queue', { keyPath: 'id' });
      syncStore.createIndex('by_status', 'syncStatus');
      syncStore.createIndex('by_type', 'type');

      // Cached data store (for read-only offline views)
      db.createObjectStore('cached_data', { keyPath: 'key' });
    },
  });

  return dbInstance;
}

// ── Sync Queue Operations ──

export async function queueOfflineRecord(
  type: OfflineRecordType,
  payload: Record<string, unknown>
): Promise<string> {
  const db = await getOfflineDB();
  const id = `offline_${type}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const record: OfflineRecord = {
    id,
    type,
    payload,
    createdAt: new Date().toISOString(),
    syncStatus: 'pending',
    syncAttempts: 0,
  };
  await db.put('sync_queue', record);
  return id;
}

export async function getPendingRecords(): Promise<OfflineRecord[]> {
  const db = await getOfflineDB();
  return db.getAllFromIndex('sync_queue', 'by_status', 'pending');
}

export async function getFailedRecords(): Promise<OfflineRecord[]> {
  const db = await getOfflineDB();
  return db.getAllFromIndex('sync_queue', 'by_status', 'failed');
}

export async function getAllQueuedRecords(): Promise<OfflineRecord[]> {
  const db = await getOfflineDB();
  const all = await db.getAll('sync_queue');
  return all.filter((r) => r.syncStatus !== 'synced');
}

export async function markRecordSyncing(id: string): Promise<void> {
  const db = await getOfflineDB();
  const record = await db.get('sync_queue', id);
  if (!record) return;
  await db.put('sync_queue', { ...record, syncStatus: 'syncing', syncAttempts: record.syncAttempts + 1 });
}

export async function markRecordSynced(id: string): Promise<void> {
  const db = await getOfflineDB();
  const record = await db.get('sync_queue', id);
  if (!record) return;
  await db.put('sync_queue', { ...record, syncStatus: 'synced', syncedAt: new Date().toISOString() });
}

export async function markRecordFailed(id: string, error: string): Promise<void> {
  const db = await getOfflineDB();
  const record = await db.get('sync_queue', id);
  if (!record) return;
  await db.put('sync_queue', { ...record, syncStatus: 'failed', syncError: error });
}

export async function deleteRecord(id: string): Promise<void> {
  const db = await getOfflineDB();
  await db.delete('sync_queue', id);
}

export async function clearSyncedRecords(): Promise<void> {
  const db = await getOfflineDB();
  const all = await db.getAll('sync_queue');
  const synced = all.filter((r) => r.syncStatus === 'synced');
  await Promise.all(synced.map((r) => db.delete('sync_queue', r.id)));
}

// ── Cached Data Operations ──

export async function setCachedData(key: string, data: unknown, ttlMs = 30 * 60 * 1000): Promise<void> {
  const db = await getOfflineDB();
  await db.put('cached_data', { key, data, cachedAt: new Date().toISOString(), ttlMs });
}

export async function getCachedData<T>(key: string): Promise<T | null> {
  const db = await getOfflineDB();
  const entry = await db.get('cached_data', key);
  if (!entry) return null;
  const age = Date.now() - new Date(entry.cachedAt).getTime();
  if (age > entry.ttlMs) {
    await db.delete('cached_data', key);
    return null;
  }
  return entry.data as T;
}
